// UI Mapping - Traduction des clés DB vers labels français avec emojis et couleurs

// =============================================================================
// FAMILY CONFIG - Source de vérité principale (depuis document_taxonomy)
// =============================================================================
export const FAMILY_CONFIG: Record<string, { label: string; emoji: string; color: string }> = {
  revenue:        { label: "Revenus",       emoji: "💰", color: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400" },
  identity:       { label: "Identité",      emoji: "🪪", color: "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400" },
  housing:        { label: "Logement",      emoji: "🏠", color: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400" },
  banking:        { label: "Banque",        emoji: "🏦", color: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400" },
  work:           { label: "Travail",       emoji: "💼", color: "bg-gray-100 text-gray-800 dark:bg-gray-800/50 dark:text-gray-300" },
  education:      { label: "Scolarité",     emoji: "🎓", color: "bg-pink-100 text-pink-800 dark:bg-pink-900/30 dark:text-pink-400" },
  vehicle:        { label: "Véhicule",      emoji: "🚗", color: "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400" },
  health:         { label: "Santé",         emoji: "💊", color: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400" },
  consumption:    { label: "Consommation",  emoji: "🛒", color: "bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-400" },
  other:          { label: "Autre",         emoji: "📦", color: "bg-slate-100 text-slate-800 dark:bg-slate-800/50 dark:text-slate-300" },
};

// Mapping category legacy → family (pour les docs sans taxonomy_slug)
export const CATEGORY_TO_FAMILY: Record<string, string> = {
  housing:        "housing",
  vehicle:        "vehicle",
  health:         "health",
  insurance:      "consumption",  // assurance conso → consumption
  work:           "work",
  accounting:     "revenue",      // comptabilité → revenus
  banking:        "banking",
  administrative: "identity",     // administratif → identité
  legal:          "identity",     // juridique → identité
  education:      "education",
  tech:           "consumption",  // tech → consommation
  other:          "other",
};

// =============================================================================
// Configuration des Catégories (LEGACY - pour rétrocompatibilité)
// =============================================================================
export const CATEGORY_CONFIG: Record<string, { label: string; emoji: string; color: string }> = {
  housing:        { label: "Logement",      emoji: "🏠", color: "bg-blue-100 text-blue-800" },
  vehicle:        { label: "Véhicule",      emoji: "🚗", color: "bg-orange-100 text-orange-800" },
  health:         { label: "Santé",         emoji: "💊", color: "bg-green-100 text-green-800" },
  insurance:      { label: "Assurance",     emoji: "🛡️", color: "bg-indigo-100 text-indigo-800" },
  work:           { label: "Travail",       emoji: "💼", color: "bg-gray-100 text-gray-800" },
  accounting:     { label: "Comptabilité",  emoji: "📊", color: "bg-emerald-100 text-emerald-800" },
  banking:        { label: "Banque",        emoji: "💰", color: "bg-yellow-100 text-yellow-800" },
  administrative: { label: "Administratif", emoji: "📂", color: "bg-purple-100 text-purple-800" },
  legal:          { label: "Juridique",     emoji: "⚖️", color: "bg-red-100 text-red-800" },
  education:      { label: "Scolarité",     emoji: "🎓", color: "bg-pink-100 text-pink-800" },
  tech:           { label: "Tech & Maison", emoji: "📱", color: "bg-cyan-100 text-cyan-800" },
  other:          { label: "Autre",         emoji: "📦", color: "bg-slate-100 text-slate-800" }
};

// Configuration des Types de documents (Label + Emoji)
export const TYPE_CONFIG: Record<string, { label: string; emoji: string }> = {
  // Types financiers
  invoice:        { label: "Facture",             emoji: "💸" },
  receipt:        { label: "Reçu",                emoji: "🧾" },
  expense_report: { label: "Note de frais",       emoji: "🍽️" },
  quote:          { label: "Devis",               emoji: "🤝" },
  
  // Types administratifs & Juridiques
  tax:            { label: "Impôts/Taxes",        emoji: "🏛️" },
  contract:       { label: "Contrat",             emoji: "✍️" },
  insurance:      { label: "Contrat Assurance",   emoji: "🛡️" },
  identity:       { label: "Pièce d'identité",    emoji: "🪪" },
  attestation:    { label: "Attestation",         emoji: "📋" },
  kbis:           { label: "Kbis",                emoji: "🏢" },
  gray_card:      { label: "Carte Grise",         emoji: "🚘" },
  notarial_deed:  { label: "Acte notarié",        emoji: "⚖️" },
  
  // Types Vie quotidienne
  payslip:             { label: "Bulletin de salaire", emoji: "💶" },
  statement:           { label: "Relevé / Bilan",      emoji: "🏦" },
  bank_statement:      { label: "Relevé bancaire",     emoji: "🏦" },
  prescription:        { label: "Ordonnance",          emoji: "🩺" },
  medical_certificate: { label: "Certificat médical",  emoji: "🏥" },
  certificate:         { label: "Certificat / Diplôme",emoji: "📜" },
  warranty:            { label: "Garantie / SAV",      emoji: "🏷️" },
  justification:       { label: "Justificatif",        emoji: "📍" },
  
  // Types professionnels
  cv:                  { label: "CV / Curriculum Vitae", emoji: "📄" },
  
  // Fallbacks
  administrative: { label: "Administratif",       emoji: "📄" },
  other:          { label: "Autre",               emoji: "📝" }
};

// =============================================================================
// Helper functions
// =============================================================================

// Get family config
export const getFamilyConfig = (family: string | null | undefined) => {
  return FAMILY_CONFIG[family || 'other'] || FAMILY_CONFIG.other;
};

// Get category config (legacy)
export const getCategoryConfig = (category: string | null | undefined) => {
  return CATEGORY_CONFIG[category || 'other'] || CATEGORY_CONFIG.other;
};

export const getTypeConfig = (docType: string | null | undefined) => {
  return TYPE_CONFIG[docType || 'other'] || TYPE_CONFIG.other;
};

/**
 * Détermine la famille d'affichage d'un document (stratégie hybride)
 * - Si taxonomy_slug existe ET on a family dans les données → utilise family
 * - Sinon fallback sur category → family via CATEGORY_TO_FAMILY
 * 
 * @param doc - Document avec potentiellement taxonomy_slug, family (from join), et category
 * @param taxonomyFamily - La famille venant de document_taxonomy (si jointure faite)
 */
export const getDisplayFamily = (
  doc: { taxonomy_slug?: string | null; category?: string | null },
  taxonomyFamily?: string | null
): string => {
  // Si on a taxonomy_slug et family (depuis taxonomie), utiliser family
  if (doc.taxonomy_slug && taxonomyFamily) {
    return taxonomyFamily;
  }
  
  // Fallback: mapper category vers family
  if (doc.category) {
    return CATEGORY_TO_FAMILY[doc.category] || 'other';
  }
  
  return 'other';
};

/**
 * Version simplifiée: dérive family depuis category quand on n'a pas de jointure taxonomie
 */
export const getFamilyFromCategory = (category: string | null | undefined): string => {
  if (!category) return 'other';
  return CATEGORY_TO_FAMILY[category] || 'other';
};

// Get category label with emoji (legacy)
export const getCategoryLabel = (category: string | null | undefined, withEmoji = true): string => {
  const config = getCategoryConfig(category);
  return withEmoji ? `${config.emoji} ${config.label}` : config.label;
};

// Get family label with emoji
export const getFamilyLabel = (family: string | null | undefined, withEmoji = true): string => {
  const config = getFamilyConfig(family);
  return withEmoji ? `${config.emoji} ${config.label}` : config.label;
};

// Get type label with emoji
export const getTypeLabel = (docType: string | null | undefined, withEmoji = true): string => {
  const config = getTypeConfig(docType);
  return withEmoji ? `${config.emoji} ${config.label}` : config.label;
};
